// Analysebrett wie auf lichess: Zugbaum mit Varianten, Stockfish mit drei Linien,
// Bewertungsbalken, Pfeile, Tastatur wie lila (ui/analyse/src/keyboard.ts).
import { h, type VNode } from 'snabbdom';
import { Chess } from 'chessops/chess';
import { chessgroundDests } from 'chessops/compat';
import { makeFen, parseFen } from 'chessops/fen';
import { makeSanAndPlay } from 'chessops/san';
import { parseSquare, parseUci } from 'chessops/util';
import type { DrawShape } from '@lichess-org/chessground/draw';
import type { Key } from '@lichess-org/chessground/types';
import { api, type GameDetail, type ReviewResult } from '../api';
import { comment, GLYPH, judgementOf, reviewBox } from './review';
import { board } from '../board';
import { Ceval, formatEval, winningChances, type EngineLine } from '../ceval';
import * as tree from '../tree';
import type { Node } from '../tree';
import { debounce, fullMoves, inFocus, isPublic, maxDepth, setFocus } from '../util';
import { icon } from '../icons';
import { loadPref, savePref } from '../prefs';
import { materialLead } from '../material';
import { playerBar } from './playerbar';
import { promotionView } from './promotion';
import { gameLinks } from './gamelinks';
import { locale, t } from '../i18n';

const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

export class AnalyseCtrl {
  /** Partie aus der Bibliothek, oder null bei freier Analyse */
  gameId: number | null = null;
  data: GameDetail | null = null;
  freeFen: string | null = null;
  root: Node = tree.root(START_FEN);
  node: Node = this.root;
  flipped = false;
  error = '';
  saveError = '';
  engineOn = loadPref('engine', true);
  arrows = loadPref('arrows', true);
  infinite = loadPref('infinite', false);
  threat = false;
  help = false;
  promotion: { orig: Key; dest: Key; color: 'white' | 'black' } | null = null;
  ceval: Ceval;
  review: ReviewResult | null = null;
  private reviewTimer: number | undefined;

  constructor(readonly redraw: () => void) {
    this.ceval = new Ceval(redraw, () => ({ multiPv: 3, depth: this.infinite ? 0 : 22 }));
  }

  async openGame(id: number) {
    if (this.gameId === id && this.data) return;
    this.gameId = id;
    this.freeFen = null;
    this.data = null;
    this.error = '';
    this.redraw();
    try {
      this.data = await api.game(id);
      this.review = null;
      this.loadReview();
      this.root = tree.fromPlies(this.data.plies);
      tree.addLines(this.root, this.data.variations ?? []);
      this.node = this.root;
      this.onPosition();
    } catch (e) {
      this.error = t('Partie nicht geladen: {e}', { e: (e as Error).message });
    }
    this.redraw();
  }

  openFen(fen: string, flipped = false) {
    this.flipped = flipped;
    if (this.gameId === null && this.freeFen === fen) return;
    this.gameId = null;
    this.review = null;
    clearTimeout(this.reviewTimer);
    this.data = null;
    this.error = '';
    try {
      Chess.fromSetup(parseFen(fen).unwrap()).unwrap();
    } catch {
      this.error = t('Die Stellung (FEN) ist ungültig.');
      this.redraw();
      return;
    }
    this.freeFen = fen;
    this.root = tree.root(fen);
    this.node = this.root;
    this.onPosition();
    this.redraw();
  }

  private async loadReview() {
    clearTimeout(this.reviewTimer);
    const id = this.gameId;
    if (id === null) return;
    try {
      const r = await api.review(id);
      if (id !== this.gameId) return;
      this.review = r;
      if (r.status === 'running') this.reviewTimer = window.setTimeout(() => this.loadReview(), 1000);
    } catch (e) {
      this.saveError = t('Auswertung nicht geladen: {e}', { e: (e as Error).message });
    }
    this.redraw();
  }

  async startReview() {
    if (this.gameId === null) return;
    try {
      this.review = await api.requestReview(this.gameId);
      this.loadReview();
    } catch (e) {
      this.saveError = (e as Error).message;
      this.redraw();
    }
  }

  /** Zum i-ten Partiezug springen (0 = Ausgangsstellung) */
  jumpMain(i: number) {
    let n = this.root;
    for (let k = 0; k < i; k++) {
      const next = n.children.find(c => c.game);
      if (!next) break;
      n = next;
    }
    this.jump(n);
  }

  /** Nach jedem Stellungswechsel: Engine neu, Pfeile neu */
  private onPosition() {
    this.promotion = null;
    if (this.engineOn) this.ceval.start(this.evalFen());
    else this.ceval.stop();
  }

  /** Im Drohungsmodus rechnet die Engine so, als dürfe die Gegenseite noch einmal ziehen. */
  evalFen(): string {
    if (!this.threat) return this.node.fen;
    const setup = parseFen(this.node.fen).unwrap();
    setup.turn = setup.turn === 'white' ? 'black' : 'white';
    setup.epSquare = undefined;
    return makeFen(setup);
  }

  canThreat() {
    return !this.node.check;
  }

  jump(n: Node) {
    this.node = n;
    this.onPosition();
    this.redraw();
    requestAnimationFrame(() => document.querySelector('.tview .active')?.scrollIntoView({ block: 'nearest' }));
  }

  next() {
    if (this.node.children[0]) this.jump(this.node.children[0]);
  }
  prev() {
    if (this.node.parent) this.jump(this.node.parent);
  }
  first() {
    this.jump(this.root);
  }
  last() {
    const line = tree.lineFrom(this.node);
    this.jump(line[line.length - 1]);
  }

  /** Zug ausführen (vom Brett, aus einer Engine-Linie oder per Leertaste). */
  playUci(uci: string) {
    const child = tree.play(this.node, uci);
    if (!child) return;
    if (!child.game) this.persist();
    this.jump(child);
  }

  private onBoardMove(orig: Key, dest: Key) {
    const pos = Chess.fromSetup(parseFen(this.node.fen).unwrap()).unwrap();
    const piece = pos.board.get(parseSquare(orig)!);
    if (piece?.role === 'pawn' && (dest[1] === '8' || dest[1] === '1')) {
      this.promotion = { orig, dest, color: piece.color };
      this.redraw();
      return;
    }
    this.playUci(orig + dest);
  }

  promote(role: 'q' | 'r' | 'b' | 'n' | null) {
    const p = this.promotion;
    this.promotion = null;
    if (p && role) this.playUci(p.orig + p.dest + role);
    else this.redraw();
  }

  playBest() {
    const best = this.ceval.current?.fen === this.node.fen ? this.ceval.current.lines[0]?.pv[0] : undefined;
    if (best) this.playUci(best);
  }

  /** Linie einer Engine-Zeile bis einschließlich Zug `upto` übernehmen */
  playPv(pv: string[], upto: number) {
    let n: Node | null = this.node;
    for (const uci of pv.slice(0, upto + 1)) {
      n = n && tree.play(n, uci);
    }
    if (n) {
      this.persist();
      this.jump(n);
    }
  }

  deleteVariation() {
    const n = this.node;
    if (n.game || !n.parent) return;
    // bis zum Anfang der eigenen Variante zurück, damit nicht nur ein Endstück fällt
    let start = n;
    while (start.parent && !start.parent.game && start.parent.children.length === 1) start = start.parent;
    const back = start.parent!;
    tree.remove(start);
    this.persist();
    this.jump(back);
  }

  toggleEngine() {
    this.engineOn = !this.engineOn;
    savePref('engine', this.engineOn);
    if (!this.engineOn) this.threat = false;
    this.onPosition();
    this.redraw();
  }

  toggleArrows() {
    this.arrows = !this.arrows;
    savePref('arrows', this.arrows);
    this.redraw();
  }

  toggleInfinite() {
    this.infinite = !this.infinite;
    savePref('infinite', this.infinite);
    if (this.engineOn) this.ceval.restart();
    this.redraw();
  }

  toggleThreat() {
    if (!this.engineOn || !this.canThreat()) return;
    this.threat = !this.threat;
    this.onPosition();
    this.redraw();
  }

  /** Varianten speichern (nur bei Partien aus der Bibliothek) */
  private persist = debounce(async () => {
    if (isPublic) return; // Demo: Varianten nur im Browser
    if (this.gameId === null) return;
    try {
      await api.saveAnalysis(this.gameId, tree.variationLines(this.root));
      this.saveError = '';
    } catch (e) {
      this.saveError = t('Varianten nicht gespeichert: {e}', { e: (e as Error).message });
    }
    this.redraw();
  }, 400);

  /** Engine-Pfeile wie lichess: beste Linie blau, weitere grau, Drohung rot */
  shapes(): DrawShape[] {
    const shapes: DrawShape[] = [];
    const ev = this.ceval.current;
    if (this.arrows && this.engineOn && ev && ev.fen === this.evalFen()) {
      ev.lines.forEach((l, i) => {
        const m = l.pv[0];
        if (!m) return;
        const brush = this.threat ? 'paleRed' : i === 0 ? 'paleBlue' : 'paleGrey';
        shapes.push({ orig: m.slice(0, 2) as Key, dest: arrowDest(m, ev.fen), brush, modifiers: { lineWidth: i === 0 ? 12 : 8 } });
      });
    }
    return shapes;
  }

  key(e: KeyboardEvent) {
    if (e.target instanceof Element && e.target.closest('input, textarea, select')) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const k = e.key;
    const map: Record<string, () => void> = {
      ArrowLeft: () => this.prev(),
      k: () => this.prev(),
      ArrowRight: () => this.next(),
      j: () => this.next(),
      ArrowUp: () => this.first(),
      Home: () => this.first(),
      '0': () => this.first(),
      ArrowDown: () => this.last(),
      End: () => this.last(),
      $: () => this.last(),
      ' ': () => (this.engineOn ? this.playBest() : this.toggleEngine()),
      f: () => ((this.flipped = !this.flipped), this.redraw()),
      l: () => this.toggleEngine(),
      a: () => this.toggleArrows(),
      x: () => this.toggleThreat(),
      '?': () => ((this.help = !this.help), this.redraw()),
      Escape: () => ((this.help = false), this.promote(null)),
    };
    const f = map[k];
    if (f) {
      e.preventDefault();
      f();
    }
  }

  dests() {
    const pos = Chess.fromSetup(parseFen(this.node.fen).unwrap()).unwrap();
    return { turn: pos.turn, dests: chessgroundDests(pos) };
  }

  boardMove = (orig: Key, dest: Key) => this.onBoardMove(orig, dest);
}

/** Rochade in chessops-Form (König schlägt Turm, e1h1) als Königszug zeichnen (e1g1) */
function arrowDest(uci: string, fen: string): Key {
  const [from, to] = [uci.slice(0, 2), uci.slice(2, 4)];
  const board = parseFen(fen).unwrap().board;
  const piece = board.get(parseSquare(from)!);
  const target = board.get(parseSquare(to)!);
  if (piece?.role === 'king' && target?.role === 'rook' && target.color === piece.color) {
    return ((to[0] > from[0] ? 'g' : 'c') + to[1]) as Key;
  }
  return to as Key;
}

// ---------- Darstellung ----------

function evalBar(c: AnalyseCtrl): VNode {
  const ev = c.ceval.current;
  const best = c.engineOn && !c.threat && ev?.fen === c.node.fen ? ev.lines[0] : undefined;
  const white = best ? 50 + 50 * winningChances(best) : 50;
  return h('div.evalbar', { class: { off: !best } }, [
    h('div.white', { style: { height: `${white}%` } }),
    h('div.tick', { style: { top: '50%' } }),
  ]);
}

function sanLine(fen: string, pv: string[], max: number): { san: string; label?: string }[] {
  const pos = Chess.fromSetup(parseFen(fen).unwrap()).unwrap();
  const out: { san: string; label?: string }[] = [];
  for (const [i, uci] of pv.slice(0, max).entries()) {
    const move = parseUci(uci);
    if (!move) break;
    const label = pos.turn === 'white' ? `${pos.fullmoves}.` : i === 0 ? `${pos.fullmoves}…` : undefined;
    const san = makeSanAndPlay(pos, move);
    if (san === '--') break;
    out.push({ san, label });
  }
  return out;
}

function engineBox(c: AnalyseCtrl): VNode {
  const ev = c.ceval.current;
  const lines: EngineLine[] = c.engineOn && ev && ev.fen === c.evalFen() ? ev.lines : [];
  const top = lines[0];
  const depth = top ? t('Tiefe {d}', { d: `${top.depth}${c.infinite && !isPublic ? '' : `/${maxDepth}`}` }) : c.engineOn ? t('rechnet …') : '';
  const knps = top ? `${Math.round(top.nps / 1000).toLocaleString(locale())} kn/s` : '';
  return h('div.ceval', [
    h('div.ceval-head', [
      h('label.switch', { attrs: { title: t('Engine an/aus (l)') } }, [
        h('input', { attrs: { type: 'checkbox' }, props: { checked: c.engineOn }, on: { change: () => c.toggleEngine() } }),
        h('span'),
      ]),
      c.engineOn && top && !c.threat ? h('strong.eval', formatEval(top)) : null,
      h('span.engine-name', [c.ceval.name || 'Stockfish', c.threat ? h('span.threat', ' · ' + t('Drohung')) : null]),
      h('span.muted', [depth, knps ? ` · ${knps}` : '']),
    ]),
    c.ceval.error ? h('p.error', c.ceval.error) : null,
    c.engineOn
      ? h('div.pvs', [0, 1, 2].map(i => {
          const l = lines[i];
          if (!l) return h('div.pv.empty', ' ');
          const moves = sanLine(c.evalFen(), l.pv, 12);
          return h('div.pv', [
            h('strong', formatEval(l)),
            ...moves.map((m, j) => h('span.pv-move', {
              on: { click: () => (c.threat ? undefined : c.playPv(l.pv, j)) },
            }, [m.label ? h('span.no', m.label) : null, m.san])),
          ]);
        }))
      : null,
  ]);
}

/** Zugliste im lichess-Spaltenformat; Varianten eingerückt unter dem Zug, an dem sie abzweigen. */
function treeView(c: AnalyseCtrl): VNode {
  const rows: VNode[] = [];
  const moveEl = (n: Node) => {
    const j = judgementOf(c.review, n);
    return h('button.move', { class: { active: n === c.node, variation: !n.game }, on: { click: () => c.jump(n) } }, [
      n.san ?? '',
      j ? h(`span.glyph.${j.judgement}`, GLYPH[j.judgement]) : null,
    ]);
  };

  // Hauptlinie: erste Kinder ab der Wurzel
  let n = c.root;
  let pending: Node[] = [];
  const flushVariations = (parent: Node, main: Node) => {
    const vars = parent.children.filter(x => x !== main);
    if (vars.length) rows.push(h('div.variations', vars.map(v => h('div.var', inline(c, v, true)))));
  };
  while (n.children.length) {
    const main = n.children[0];
    const isWhite = main.ply % 2 === 1;
    if (isWhite) {
      rows.push(h('span.index', `${Math.ceil(main.ply / 2)}`), moveEl(main));
      const hasVars = n.children.length > 1;
      if (hasVars) {
        rows.push(h('span.move.empty', '…'));
        flushVariations(n, main);
        pending = [];
      } else {
        pending = [main];
      }
    } else {
      if (!pending.length) rows.push(h('span.index', `${Math.ceil(main.ply / 2)}`), h('span.move.empty', '…'));
      rows.push(moveEl(main));
      flushVariations(n, main);
      pending = [];
    }
    n = main;
  }
  if (pending.length) rows.push(h('span.move.empty', ''));
  return h('div.tview', rows);
}

/** Variante als Fließtext: "12… Qe7 13. Bxb5 (13. Nd5 …)" */
function inline(c: AnalyseCtrl, start: Node, first: boolean): VNode[] {
  const out: VNode[] = [];
  let n: Node | undefined = start;
  let label = first;
  while (n) {
    const isWhite = n.ply % 2 === 1;
    if (isWhite || label) out.push(h('span.no', tree.moveLabel(n)));
    const cur: Node = n;
    out.push(h('button.move', { class: { active: cur === c.node, variation: !cur.game }, on: { click: () => c.jump(cur) } }, cur.san ?? ''));
    label = false;
    const parent: Node | undefined = cur.children.length ? cur : undefined;
    if (!parent) break;
    const [main, ...rest] = parent.children;
    for (const r of rest) out.push(h('span.sub', ['(', ...inline(c, r, true), ')']));
    if (rest.length) label = true;
    n = main;
  }
  return out;
}

function helpView(c: AnalyseCtrl): VNode | null {
  if (!c.help) return null;
  const keys: [string, string][] = [
    [t('← →  oder  k j'), t('Zug zurück / vor')],
    [t('↑ ↓  oder  0 $'), t('Anfang / Ende')],
    [t('Leertaste'), t('besten Zug spielen (Engine aus: einschalten)')],
    ['l', t('Engine an / aus')],
    ['a', t('Engine-Pfeile an / aus')],
    ['x', t('Drohung zeigen')],
    ['f', t('Brett drehen')],
    ['z', t('Vollbild')],
    ['?', t('diese Hilfe')],
  ];
  return h('div.help', { on: { click: () => ((c.help = false), c.redraw()) } }, [
    h('div.help-box', [h('h2', t('Tastatur')), h('table', keys.map(([k, t]) => h('tr', [h('td.k', k), h('td', t)])))]),
  ]);
}

/** Titel für das Kopfband: Spieler und Partiedaten, bei freier Analyse die FEN */
export function analyseHead(c: AnalyseCtrl): { title: VNode | string; lead: VNode | string } {
  const g = c.data?.game;
  if (c.gameId !== null && !g) return { title: t('Partie'), lead: t('lade …') };
  if (!g) return { title: t('Freie Analyse'), lead: h('code.fen', c.root.fen) };
  const meta = [g.event, g.site !== g.event ? g.site : null, g.date?.replace(/\.\?\?/g, '')].filter(Boolean).join(' · ');
  return {
    title: h('span', [g.white ?? '?', h('span.dash', ' – '), g.black ?? '?']),
    lead: [meta, g.result, t('{n} Züge', { n: fullMoves(g.plies) })].filter(Boolean).join(' · '),
  };
}

/** Leiste über/unter dem Brett: wer dort sitzt, wer am Zug ist, Materialvorsprung; unten der aktuelle Zug */
function bar(c: AnalyseCtrl, where: 'top' | 'bottom'): VNode {
  const g = c.data?.game;
  const color = (where === 'bottom') !== c.flipped ? 'white' : 'black';
  const lead = materialLead(c.node.fen) * (color === 'white' ? 1 : -1);
  const move = c.node.san ? `${tree.moveLabel(c.node)} ${c.node.san}` : '';
  return playerBar(where, {
    color,
    name: g ? (color === 'white' ? g.white : g.black) ?? '?' : color === 'white' ? t('Weiß') : t('Schwarz'),
    active: c.node.fen.split(' ')[1] === color[0],
    lead,
    info: where === 'bottom' ? move : g?.year ? String(g.year) : '',
  });
}

export function analyseView(c: AnalyseCtrl): VNode {
  if (c.error) return h('p.error', c.error);
  if (c.gameId !== null && !c.data) return h('p.muted', t('lade Partie …'));
  const d = c.data;
  const g = d?.game;
  const { turn, dests } = c.dests();
  const lastMove = c.node.uci ? ([c.node.uci.slice(0, 2), c.node.uci.slice(2, 4)] as Key[]) : undefined;
  // Rochade in chessops-Form (e1h1) für die Anzeige als Königszug markieren
  if (lastMove && c.node.san?.startsWith('O-O')) {
    lastMove[1] = ((lastMove[1] > lastMove[0] ? 'g' : 'c') + lastMove[1][1]) as Key;
  }
  return h('section.analyse', [
    h('div.analyse-body', [
      evalBar(c),
      h('div.board-col', [
        bar(c, 'top'),
        board('analyse-board', {
          fen: c.node.fen,
          turnColor: turn,
          check: c.node.check ? turn : false,
          lastMove,
          orientation: c.flipped ? 'black' : 'white',
          coordinates: true,
          animation: { enabled: true, duration: 250 },
          movable: { free: false, color: turn, dests, showDests: true, events: { after: c.boardMove } },
          premovable: { enabled: false },
          draggable: { enabled: true, showGhost: true },
          highlight: { lastMove: true, check: true },
          drawable: { enabled: true, visible: true, autoShapes: inFocus() ? [] : c.shapes() },
        }),
        c.promotion ? promotionView(c.promotion.color, r => c.promote(r)) : null,
        bar(c, 'bottom'),
      ]),
      h('div.side', [
        engineBox(c),
        comment(c.review, c.node),
        treeView(c),
        h('div.ctl', [
          h('button.btn', { attrs: { title: t('Anfang (↑)') }, on: { click: () => c.first() } }, icon('first')),
          h('button.btn', { attrs: { title: t('Zurück (←)') }, on: { click: () => c.prev() } }, icon('prev')),
          h('button.btn', { attrs: { title: t('Vor (→)') }, on: { click: () => c.next() } }, icon('next')),
          h('button.btn', { attrs: { title: t('Ende (↓)') }, on: { click: () => c.last() } }, icon('last')),
          h('button.btn', { attrs: { title: t('Brett drehen (f)') }, on: { click: () => ((c.flipped = !c.flipped), c.redraw()) } }, icon('flip')),
          h('button.btn', { attrs: { title: t('Vollbild (z)') }, on: { click: () => setFocus(true, c.redraw) } }, icon('fullscreen')),
        ]),
        h('div.tools', [
          !c.node.game ? h('button.link', { on: { click: () => c.deleteVariation() } }, t('Variante löschen')) : null,
          // Demo: der Server begrenzt die Tiefe, die Wahl wäre wirkungslos
          isPublic ? null : h('label.chk', { attrs: { title: t('Ohne Grenze rechnen, bis du weiterziehst') } }, [
            h('input', { attrs: { type: 'checkbox' }, props: { checked: c.infinite }, on: { change: () => c.toggleInfinite() } }),
            ' ' + t('unbegrenzt rechnen'),
          ]),
          h('a.link', { attrs: { href: `#/editor/${encodeURIComponent(c.node.fen)}` } }, t('Stellung bearbeiten')),
          ...gameLinks(c),
          h('button.link', { on: { click: () => ((c.help = true), c.redraw()) } }, t('Tasten (?)')),
        ]),
        g
          ? reviewBox(c.review, c.node, { white: g.white ?? '?', black: g.black ?? '?' }, {
              start: () => c.startReview(),
              jump: i => c.jumpMain(i),
            }, c.root.fen.split(' ')[1] !== 'b')
          : null,
        c.saveError ? h('p.error', c.saveError) : null,
        d?.error ? h('p.warn', t('Die PGN enthält einen ungültigen Zug. Gezeigt wird die Partie bis dahin. ({e})', { e: d.error })) : null,
        d ? h('details.pgn', [h('summary', 'PGN'), h('pre', d.pgn)]) : null,
      ]),
    ]),
    helpView(c),
  ]);
}
